import { useState, useRef, useEffect, type MouseEvent } from 'react';
import { SUPPORTED_LANGS } from '../i18n';
import { buildForLang, navigate, useRoute, type ModeKey } from '../router';
import type { Lang } from '../lib/countries';

const NAMES: Record<Lang, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  it: 'Italiano',
  tr: 'Türkçe',
};

// 右上角精致语言切换器(古地图风下拉)
export default function LangSwitcher({ lang }: { lang: Lang; mode?: ModeKey }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const route = useRoute();

  useEffect(() => {
    const h = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  function go(e: MouseEvent, l: Lang) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    navigate(buildForLang(l, route));
    setOpen(false);
  }

  return (
    <div className="lang" ref={ref}>
      <button
        className="lang-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
      >
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M3 12h18M12 3c2.4 2.6 3.6 5.7 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.7-3.6-9s1.2-6.4 3.6-9Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          />
        </svg>
        <span className="lang-code">{lang.toUpperCase()}</span>
        <svg className="lang-caret" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="lang-menu" role="listbox">
          {SUPPORTED_LANGS.map((l) => (
            <li key={l}>
              <a
                href={buildForLang(l, route)}
                className={l === lang ? 'on' : ''}
                onClick={(e) => go(e, l)}
              >
                <span className="lc">{l.toUpperCase()}</span>
                <span className="ln">{NAMES[l]}</span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
